import React, {useEffect, useReducer, useRef, useState} from 'react';
import {createRefetchContainer, graphql} from 'react-relay';
import Moment from 'moment';
import moment from 'moment';
import {cloneDeep} from 'lodash';
import {withRouter} from 'react-router-dom';
import * as tracking from '../../../../tracking';
import {TimeRowTableWrapper} from '../timesheets_styled';
import {TIME_PERIOD} from '../timesheets_change_date_view';
import ActionsMenu from '../../../shared/components/action-menu/actions_menu';
import {
	getNavigationString,
	ProjectIndicator
} from '../../../shared/components/project-indicator/project_group_indicator';
import StarTask from '../../../shared/components/star/star_task';
import Util from '../../../shared/util/util';
import Warning from '../../../../components/warning';
import {BUTTON_COLOR, BUTTON_STYLE, EMPTY_STATE, PROJECT_STATUS} from '../../../../constants';
import HarvestPopOut from '../../../shared/components/popups/harvest_pop_out';
import Unit4PopOut from '../../../shared/components/popups/unit4_pop_out';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import HoursInputView from '../../../shared/components/inputs/hours-input/hours_input_view';
import {withSocketHandling} from '../../../../socket/withSocketHandling';
import {getSocketConfig} from '../timesheet_socket_handling';
import {
	addTimeRegTypes,
	buildWeekArr,
	COLUMN_NAME,
	filterTimeregistrations,
	getInfoTextForHoursInput,
	getTimeEntryApprovalStatus,
	groupRegs,
	lineItemWithNotes,
	sortList,
	TIME_REG_TYPES,
	timeEntryContainsHarvestTask,
	timeEntryContainsNotes,
	timeEntryInLockedPeriod,
	timeEntryInvoiced,
	timeEntryNoAccess,
	timeEntryProjectDone,
	timeEntryProjectHalted,
} from './time_row_utils';
import {constructSearchQuery} from './timesheets_data_fetch_util';
import {
	getTimeRegistrationsOfTheDay,
	separateWorkingTimeAndTimeOff
} from '../timesheets_time_registrations';
import {
	getHolidaysOfTheDay,
	workingHourForTheDay
} from '../timesheets_person_data';
import UpdateTimeRegistration from '../../../../mutations/update_time_registration_mutation.modern';
import CreateTimeRegistration from '../../../../mutations/create_time_registration_mutation.modern';
import DeleteAllTimeRegistrationsForDay from '../../../../mutations/delete_all_time_registrations_for_day_mutation';
import DeleteAllTimeRegistrationsForWeek from '../../../../mutations/delete_all_time_registrations_for_week_mutation';
import NotesPopOut from '../../../shared/components/popups/notes_pop_out';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import TextAreaFoldout from '../../../shared/components/inputs/textarea_foldout';
import DeleteTimeRegistration from '../../../../mutations/delete_time_registration_mutation.modern';
import EmptyState from '../../../shared/components/empty-states/empty_state';
import CheckMarkAndTime from '../../../shared/components/checkmark-time/checkmark-time';
import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import AiIcon from '../../../../images/ico-ai';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import SendRecommenderFeedbackMutation from '../../../../mutations/send_recommender_feedback_mutation';

const timesheetsTimeRowTable = ({
	intl,
	relay,
	retry,
	history,
	viewer,
	currentViewingDate,
	timeRegistrations,
	timeRegsForPeriod,
	weekStart,
	weekEnd,
	timePeriod,
	enabledColumns,
	selectedPerson,
	selectedSwitch,
	searchValue,
	selectDayFromCalendar,
	holidays,
	setSocketConfig,
	setSocketVariables,
	lockedDate,
	isFullyLocked,
	usingTimeApproval,
	periodTimeApprovalStatus,
}) => {
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const [focusedInput, setFocusedInput] = useState();
	const [sortValue, setSortValue] = useState(localStorage.getItem('time-page-sort') ? localStorage.getItem('time-page-sort') : COLUMN_NAME.PROJECT_INDICATOR);
	const [sortAscending, setSortAscending] = useState(localStorage.getItem('time-page-sort-ascending') || true);
	const [timeRegToFocus, setTimeRegToFocus] = useState();
	const [movedTasks, setMovedTasks] = useState([]);
	const [movedIdleTimes, setMovedIdleTimes] = useState([]);
	const [harvestTasks, setHarvestTasks] = useState([]);
	const [harvestLoading, setHarvestLoading] = useState([]);
	const [harvestProjectLoaded, setHarvestProjectLoaded] = useState(null);
	const [unit4Activities, setUnit4Activities] = useState([]);
	const [unit4Loading, setUnit4Loading] = useState([]);
	const [unit4ProjectLoaded, setUnit4ProjectLoaded] = useState(null);

	const [switchTasksLoading, setSwitchTasksLoading] = useState(false);

	// Hook hack to allow forceUpdate
	const [forceUpdate, setForceUpdate] = useReducer(x => x + 1, 0);

	const didMount = useRef(false);
	const doneFetching = useRef(true);
	const refetchVariables = useRef(null);

	const refetch = (variables, callback, options) => {
		relay.refetch(variables, null, callback, options);
		refetchVariables.current = variables;
		setSocketVariables(variables);
	};

	const refetchTable = () => {
		if (refetchVariables.current) {
			relay.refetch(refetchVariables.current);
		} else {
			retry();
		}
	};

	useEffect(() => {
		const selectedPersonIdInt = parseInt(atob(selectedPerson.id).replace('Person:', ''));
		setSocketConfig(getSocketConfig(selectedPersonIdInt), relay.refetch);
		subscribe(EVENT_ID.SCHEDULING_MODAL_MUTATION_SUCCESS, refetchTable);
		dispatch(EVENT_ID.PAGE_LOADED, 'timesheet');

		return () => {
			unsubscribe(EVENT_ID.SCHEDULING_MODAL_MUTATION_SUCCESS, refetchTable);
		};
	}, []);

	useEffect(() => {
		// Only run if not mount
		if (!didMount.current) {
			didMount.current = true;
			return;
		}
		const onSuccess = () => {
			setSwitchTasksLoading(false);
			doneFetching.current = true;
		};

		setSwitchTasksLoading(true);
		doneFetching.current = false;

		const searchQuery = constructSearchQuery(selectedSwitch, currentViewingDate, isWeekView);

		// Suggested time is queried in a completely different way, and will therefore need to be handled separately
		if (selectedSwitch === 'suggested') {
			tracking.trackElementClicked(selectedSwitch + '-checked');
			Util.localStorageSetItem('timesheets-selected-switch', selectedSwitch);

			refetch(
				{
					taskTypeSearchQuery: searchQuery,
					fetchSwitchTasks: false,
					fetchSuggestedTasks: true,
				},
				onSuccess,
				{force: true}
			);
			return;
		} else if (selectedSwitch && selectedSwitch !== 'none') {
			tracking.trackElementClicked(selectedSwitch + '-checked');
			Util.localStorageSetItem('timesheets-selected-switch', selectedSwitch);
		}

		// No switches selected
		if (searchQuery.filters.length === 0) {
			Util.localStorageSetItem('timesheets-selected-switch', 'none');

			refetch(
				{
					taskTypeSearchQuery: searchQuery,
					fetchSwitchTasks: false,
					fetchSuggestedTasks: false,
				},
				onSuccess,
				{force: true}
			);
		} else {
			refetch(
				{
					taskTypeSearchQuery: searchQuery,
					fetchSwitchTasks: true,
					fetchSuggestedTasks: false,
				},
				onSuccess,
				{force: true}
			);
		}
	}, [selectedSwitch]);

	const suggestedSwitchTasks = viewer.timeRegistrationSuggestedTasks
		? cloneDeep(viewer.timeRegistrationSuggestedTasks)
				.sort(function (a, b) {
					return Number(b.total) - Number(a.total);
				})
				.slice(0, 5)
		: [];

	let switchTasks = viewer.company.taskTypeQueryTasks ? viewer.company.taskTypeQueryTasks.edges : [];
	const switchIdleTimes = viewer.company.taskTypeQueryIdleTimes && (selectedSwitch === 'suggested' || selectedSwitch === 'starred') ? viewer.company.taskTypeQueryIdleTimes.edges.filter(idleTime => !idleTime.node.disabled) : [];

	useEffect(() => {
		if (doneFetching.current && (suggestedSwitchTasks.length !== 0 || switchTasks.length !== 0 || switchIdleTimes.length !== 0)) {
			dispatch(EVENT_ID.SWITCH_EMPTY, '');
		} else if (doneFetching.current) {
			dispatch(EVENT_ID.SWITCH_EMPTY, selectedSwitch ? selectedSwitch : '');
		}
	}, [doneFetching.current]);

	const hourInputs = [];

	useEffect(() => {
		focusedInput != null && hourInputs[focusedInput] ? hourInputs[focusedInput].focus() : void 0;
	}, [focusedInput]);

	const addMovedTask = taskId => {
		const curMovedTasks = [...movedTasks];
		curMovedTasks.push(taskId);
		setMovedTasks(curMovedTasks);
	};

	const addMovedIdleTime = idleTimeId => {
		const curMovedIdleTimes = [...movedIdleTimes];
		curMovedIdleTimes.push(idleTimeId);
		setMovedIdleTimes(curMovedIdleTimes);
	};

	useEffect(() => {
		// Clear when changing selected switches, currentViewingDate, or timePeriod
		setMovedTasks([]);
		setMovedIdleTimes([]);
	}, [selectedSwitch, currentViewingDate, timePeriod, isFullyLocked, sortValue]);

	/**
	 *
	 * @param {Moment} weekStart start date of week
	 * @param {Moment} weekEnd end date of week
	 * @param {object} timeRegistrations
	 * @param {object} selectedPerson
	 * @returns {object} object contaning members time {number} (timeworked), hoursToWork {number} (hours the selected person has to work), isHoliday {boolean}
	 */
	const getTimeAndWorkingHoursForPeriod = (weekStart, weekEnd, timeRegistrations, selectedPerson) => {
		// clone weekstart because we are going to modify it
		const day = moment(weekStart);
		const timeAndWorkingHours = [];
		do {
			const timeRegistrationsForDay = getTimeRegistrationsOfTheDay(day, timeRegistrations);
			const timeWorked = separateWorkingTimeAndTimeOff(timeRegistrationsForDay);
			const workingHours = workingHourForTheDay(selectedPerson, day, true);
			const isHoliday = getHolidaysOfTheDay(selectedPerson, day).length > 0;

			// if is holiday: hours to work should be 0, otherwise set it to working hours
			const hoursToWork = isHoliday ? 0 : workingHours;
			timeAndWorkingHours.push({
				time: timeWorked.workingTime + timeWorked.timeOff,
				hoursToWork,
				isHoliday,
				noOfTimeRegistrations: timeRegistrationsForDay.length,
				day: day.clone(),
			});
		} while (!day.add(1, 'days').isAfter(weekEnd, 'day'));
		return timeAndWorkingHours;
	}; // getTimeAndWorkingHoursForPeriod()

	const fetchHarvestTasks = project => {
		//If project dropdown is not empty
		if (project) {
			//If a project is selected, show only tasks that belong to that project
			if (project.id && project.harvestProject) {
				if (harvestLoading === true) return;
				if (harvestTasks.length === 0 || harvestProjectLoaded !== project.id) setHarvestLoading(true);
				setHarvestProjectLoaded(project.id);
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'harvest_tasks/' + project.id, init)
					.then(result => result.json())
					.then(tasks => {
						setHarvestTasks(tasks);
						setHarvestLoading(false);
					});
			} else {
				setHarvestTasks([]);
				setHarvestLoading(false);
			}
		} else {
			setHarvestTasks([]);
			setHarvestLoading(false);
		}
	};

	const fetchUnit4Activities = project => {
		//If project dropdown is not empty
		if (project) {
			//If a project is selected, show only tasks that belong to that project
			if (project.id && project.unit4Project) {
				if (unit4Loading === true) return;
				if (unit4Activities.length === 0 || unit4ProjectLoaded !== project.id) {
					setUnit4Loading(true);
				}
				setUnit4ProjectLoaded(project.id);
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'unit4_activities/' + project.id, init)
					.then(result => result.json())
					.then(activities => {
						setUnit4Activities(activities);
						setUnit4Loading(false);
					});
			} else {
				setUnit4Activities([]);
				setUnit4Loading(false);
			}
		} else {
			setUnit4Activities([]);
			setUnit4Loading(false);
		}
	};

	const onTimeCreateSuccess = result => {
		if (result.createTimeReg.errors && result.createTimeReg.errors.length === 1 && result.createTimeReg.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD') {
			showModal({
				type: MODAL_TYPE.WARNING_DATE_LOCKED,
			});
		} else {
			dispatch(EVENT_ID.TIME_REGISTRATION_MUTATION_SUCCESS, {timeRegisteredInHours: null, wasClickEvent: false});
			createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_created'})});
		}
	};

	const onTimeUpdateSuccess = result => {
		if (result.updateTimeReg.errors && result.updateTimeReg.errors.length === 1 && result.updateTimeReg.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD') {
			showModal({
				type: MODAL_TYPE.WARNING_DATE_LOCKED,
			});
		} else {
			createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_updated'})});
		}
	};

	const onTimeDeleteSuccess = result => {
		createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_deleted'})});
	};

	const getIsHarvestLineItem = item => {
		return item.task ? item.task.project.harvestProject != null : item.project ? item.project.harvestProject != null : item.harvestProject;
	};

	const getIsUnit4LineItem = item => {
		return item.task
			? item.task.project.unit4Project != null && item.task.project.unit4Project.activitiesEnabled
			: item.project
			? item.project.unit4Project != null && item.project.unit4Project.activitiesEnabled
			: item.unit4Project && item.unit4Project.activitiesEnabled;
	};

	const handleNotesFieldBlur = (lineItem, value, date, isTimeReg) => {
		if (lineItem.notes === value || (lineItem && !lineItem.notes && !value)) return; //Don't do anything if value did not change
		const val = value === '' ? null : value;
		const isSwitchIdleTime = lineItem.switchIdleTime;
		let taskId;
		let parentTaskId;
		let projectId;
		let idleTimeId;
		let canRegister = true;
		switch (lineItem.type) {
			case TIME_REG_TYPES.TASK_TIME: {
				taskId = lineItem.id;
				parentTaskId = lineItem.parentTaskId
				break;
			}
			case TIME_REG_TYPES.PROJECT_TIME: {
				projectId = lineItem.id;
				if (lineItem.status === 'DONE' || lineItem.status === 'HALTED') {
					canRegister = false;
				}
				break;
			}
			case TIME_REG_TYPES.IDLE_TIME: {
				idleTimeId = lineItem.id;
				break;
			}
			default: {
				// Tasks from moved tasks to timregs
				if (lineItem.companyTaskId) {
					taskId = lineItem.id;
					parentTaskId = lineItem.parentTaskId;
					projectId = lineItem.project.id;
				} else if (isSwitchIdleTime) {
					idleTimeId = lineItem.id;
				}
			}
		}
		if (canRegister) {
			if (!isTimeReg && val) {
				const createNoteTimeReg = (harvestTask, unit4Activity) => {
					Util.CommitMutation(
						CreateTimeRegistration,
						{
							source: 'Time-row-table',
							viewer: viewer,
							projectId: projectId,
							taskId: taskId,
							subTask: taskId ? !!parentTaskId : null,
							idleTimeId: idleTimeId,
							minutesRegistered: 0,
							notes: val,
							day: date.date(),
							month: date.month() + 1,
							year: date.year(),
							personId: selectedPerson.id,
							harvestTaskId: harvestTask ? harvestTask : null,
							unit4ActivityId: unit4Activity ? unit4Activity.value : null,
							unit4ActivityName: unit4Activity ? unit4Activity.label : null,
							newFormat: true,
						},
						onTimeCreateSuccess
					);
				};
				if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem)) {
					showModal({
						value: 0,
						type: MODAL_TYPE.HARVEST_TASK_SELECT,
						timeRegName: lineItem.name,
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
						callback: harvestTask => createNoteTimeReg(harvestTask, null),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this), //this.forceUpdate.bind(this)
					});
				} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem)) {
					showModal({
						value: 0,
						type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
						timeRegName: lineItem.name,
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
						callback: unit4Activity => createNoteTimeReg(null, unit4Activity),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this), //this.forceUpdate.bind(this)
					});
				} else {
					createNoteTimeReg();
				}
				if (isSwitchIdleTime) {
					addMovedIdleTime(idleTimeId);
				} else {
					addMovedTask(taskId);
				}
			} else {
				Util.CommitMutation(UpdateTimeRegistration, {source: 'Time-row-table', id: lineItem.id, notes: val, newFormat: true}, onTimeUpdateSuccess);
			}
		}
	};

	const onInputKeyDown = (e, idx) => {
		// Currently only works for week view, had some funkay behavior on day view with switch tasks enabled
		if (isWeekView && e && e.key) {
			let diff = 0;
			const key = e.key;
			switch (key) {
				case 'ArrowRight':
					diff = 1;
					break;
				case 'ArrowLeft':
					diff = -1;
					break;
				case 'ArrowDown':
					diff = 7;
					break;
				case 'ArrowUp':
					diff = -7;
					break;
				default:
			}
			if (diff !== 0) {
				const newIdx = Util.clamp(idx + diff, 0, hourInputs.length);
				setFocusedInput(newIdx);
			}
		}
	};

	const {formatMessage, formatNumber} = intl;

	const weekDays = isWeekView ? buildWeekArr(currentViewingDate.clone().endOf('week'), 7) : [];

	const filteredTimeRegs = timeRegsForPeriod.filter(tReg => (tReg.node.task ? !movedTasks.some(taskId => taskId === tReg.node.task.id) : tReg.node.idleTime ? !movedIdleTimes.some(idleTimeId => idleTimeId === tReg.node.idleTime.id) : true));

	let sortedTimeRegsForPeriod = sortList(filteredTimeRegs, sortValue, sortAscending).filter(filterTimeregistrations.bind(this, searchValue));

	const groupedRegs = isWeekView ? groupRegs(addTimeRegTypes(sortedTimeRegsForPeriod), weekDays) : [];

	const createTimeReg = (date, hours, taskId, parentTaskId, projectId, idleTimeId, selectedPerson, harvestTask, unit4Activity) => {
		if (selectedSwitch === 'suggested' && viewer.taskSuggestionsRecommender) {
			const isTaskInTopSuggestions = suggestedSwitchTasks && suggestedSwitchTasks.some(suggestion => (suggestion.task && suggestion.task.id === taskId) || (suggestion.idleTime && suggestion.idleTime.id === idleTimeId));
			// Send positive or negative feedback to the task suggestion algorithm on the backend
			Util.CommitMutation(SendRecommenderFeedbackMutation, {
				recommendationInstanceId: viewer.taskSuggestionsRecommender.recommendationInstanceId,
				recommendationId: viewer.taskSuggestionsRecommender.recommendationId,
				generation: viewer.taskSuggestionsRecommender.generation,
				timeStep: viewer.taskSuggestionsRecommender.timeStep,
				feedbackValue: isTaskInTopSuggestions ? 1.0 : 0.0,
			});
		}
		Util.CommitMutation(
			CreateTimeRegistration,
			{
				viewer: viewer,
				source: 'Time-row-table',
				projectId: projectId ? projectId : null,
				taskId: taskId ? taskId : null,
				subTask: taskId ? !!parentTaskId : null,
				idleTimeId: idleTimeId,
				minutesRegistered: hours * 60,
				day: date.date(),
				month: date.month() + 1,
				year: date.year(),
				personId: selectedPerson ? (selectedPerson.actualPersonId != null ? selectedPerson.actualPersonId : selectedPerson.id) : null,
				harvestTaskId: harvestTask ? harvestTask : null,
				unit4ActivityId: unit4Activity ? unit4Activity.value : null,
				unit4ActivityName: unit4Activity ? unit4Activity.label : null,
				newFormat: true,
			},
			onTimeCreateSuccess
		);
	};

	const handleHoursInputChange = (hours, lineItem, idx, isWeekView, timeRegMap, selectedPerson) => {
		if (isWeekView) {
			const timeForDay = timeRegMap.get(lineItem) ? timeRegMap.get(lineItem)[idx] : [];
			const hasTimeForDay = timeForDay.length > 0;
			let canRegister = true;
			let taskId;
			let parentTaskId;
			let projectId;
			let idleTimeId;
			switch (lineItem.type) {
				case TIME_REG_TYPES.TASK_TIME: {
					taskId = lineItem.id;
					parentTaskId = lineItem.parentTaskId
					if (lineItem.project.status === 'DONE' || lineItem.project.status === 'HALTED') {
						canRegister = false;
					}
					break;
				}
				case TIME_REG_TYPES.PROJECT_TIME: {
					projectId = lineItem.id;
					if (lineItem.status === 'DONE' || lineItem.status === 'HALTED') {
						canRegister = false;
					}
					break;
				}
				case TIME_REG_TYPES.IDLE_TIME: {
					idleTimeId = lineItem.id;
					break;
				}
				// TASK - Not a time reg yet
				default: {
					if (lineItem.switchIdleTime) {
						idleTimeId = lineItem.id;
						// For animations
						setTimeRegToFocus(idleTimeId);
						addMovedIdleTime(idleTimeId);
					} else {
						taskId = lineItem.id;
						parentTaskId = lineItem.parentTaskId;
						// For animations
						setTimeRegToFocus(taskId);
						addMovedTask(taskId);
					}
				}
			}
			if (!canRegister) {
				return;
			}
			const indexedDate = currentViewingDate
				.clone()
				.endOf('week')
				.subtract(6 - idx, 'days');
			// Don't create, always delete time reg when user types specifically 0
			if (!hasTimeForDay) {
				if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem) && hours > 0) {
					showModal({
						value: hours,
						type: MODAL_TYPE.HARVEST_TASK_SELECT,
						timeRegName: lineItem.name,
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
						callback: harvestTask => createTimeReg(indexedDate, hours, taskId, parentTaskId, projectId, idleTimeId, selectedPerson, harvestTask, null),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this), //this.forceUpdate.bind(this)
					});
				} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem) && hours > 0) {
					showModal({
						value: hours,
						type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
						timeRegName: lineItem.name,
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : lineItem,
						callback: unit4Activity => createTimeReg(indexedDate, hours, taskId, parentTaskId, projectId, idleTimeId, selectedPerson, null, unit4Activity),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this), //this.forceUpdate.bind(this)
					});
				} else {
					createTimeReg(indexedDate, hours, taskId, parentTaskId, projectId, idleTimeId, selectedPerson);
				}
			} else {
				const isLocked = timeForDay.some(el => el.node.invoiced || el.node.economicTimeId || el.node.xeroInvoiceId || el.node.lockedInPeriod);
				if (isLocked) {
					return;
				} else if (hours === null) {
					const onSuccess = result => {
						if (result.deleteAllTimeRegsForDay.errors && result.deleteAllTimeRegsForDay.errors.length === 1 && result.deleteAllTimeRegsForDay.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD') {
							showModal({
								type: MODAL_TYPE.WARNING_DATE_LOCKED,
							});
						}
					};
					Util.CommitMutation(DeleteAllTimeRegistrationsForDay, {ids: timeForDay.map(tReg => tReg.node.id)}, onSuccess);
				} else {
					const anyNote = timeForDay.length > 1 && timeForDay.some(el => el.node.notes && el.node.notes.length > 0);
					if (anyNote) {
						return;
					}
					Util.CommitMutation(
						UpdateTimeRegistration,
						{
							source: 'Time-row-table',
							id: timeForDay[0].node.id,
							day: indexedDate.date(),
							month: indexedDate.clone().month() + 1,
							year: indexedDate.year(),
							projectId: projectId,
							taskId: taskId,
							idleTimeId: idleTimeId,
							minutesRegistered: hours * 60,
							removeOthersForTheDay: true,
							newFormat: true,
						},
						onTimeUpdateSuccess
					);
				}
			}
		} else {
			const isTimeReg = lineItem.type != null;
			const isSwitchIdleTime = lineItem.switchIdleTime;
			const isExistingTimeReg = isTimeReg && lineItem.minutesRegistered !== undefined && lineItem.id;
			const date = currentViewingDate.clone();
			const task = !isSwitchIdleTime ? lineItem : null;
			const project = !isSwitchIdleTime ? lineItem.project : null;
			const idleTime = !isSwitchIdleTime ? lineItem.idleTime : lineItem;
			if (isExistingTimeReg) {
				const timeReg = lineItem;
				if (hours === null) {
					// TODO, validate this is okay?
					Util.CommitMutation(
						DeleteTimeRegistration,
						{id: timeReg.id, taskId: task ? task.id : undefined, projectId: project ? project.id : undefined, idleTimeId: idleTime ? idleTime.id : undefined, companyId: viewer.company.id},
						onTimeDeleteSuccess
					);
				} else {
					Util.CommitMutation(
						UpdateTimeRegistration,
						{
							source: 'Time-row-table',
							id: timeReg.id,
							projectId: timeReg.task ? timeReg.task.project.id : timeReg.project ? timeReg.project.id : null,
							taskId: timeReg.task ? timeReg.task.id : null,
							idleTimeId: timeReg.idleTime ? timeReg.idleTime.id : null,
							minutesRegistered: hours * 60,
							newFormat: true,
						},
						onTimeUpdateSuccess
					);
				}
			} else {
				const createTimeCallback = (harvestTask, unit4Activity) => {
					if (!isTimeReg) {
						if (isSwitchIdleTime) {
							addMovedIdleTime(idleTime.id);
						} else {
							addMovedTask(task.id);
						}
					}
					createTimeReg(date, hours, task ? task.id : null, task ? task.parentTaskId : null,project ? project.id : null, idleTime ? idleTime.id : null, selectedPerson, harvestTask, unit4Activity);
				};
				if (viewer.company.harvestEnabled && viewer.harvestUser && getIsHarvestLineItem(lineItem) && hours > 0) {
					showModal({
						value: hours,
						type: MODAL_TYPE.HARVEST_TASK_SELECT,
						timeRegName: lineItem.task ? lineItem.task.name : lineItem.project ? lineItem.project.name : '',
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project,
						callback: harvestTask => createTimeCallback(harvestTask, null),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this), //this.forceUpdate.bind(this)
					});
				} else if (viewer.company.unit4Enabled && selectedPerson.unit4User && getIsUnit4LineItem(lineItem) && hours > 0) {
					showModal({
						value: hours,
						type: MODAL_TYPE.UNIT4_ACTIVITY_SELECT,
						timeRegName: lineItem.task ? lineItem.task.name : lineItem.project ? lineItem.project.name : '',
						timeReg: lineItem,
						project: lineItem.task ? lineItem.task.project : lineItem.project,
						callback: unit4Activity => createTimeCallback(null, unit4Activity),
						defaultCallback: setForceUpdate.bind(this),
						cancelCallback: setForceUpdate.bind(this),
					});
				} else {
					createTimeCallback(null);
				}
			}
		}
	};

	const changeSortValue = col => {
		if (sortValue === col) {
			Util.localStorageSetItem('time-page-sort-ascending', !sortAscending);
			setSortAscending(!sortAscending);
		} else {
			Util.localStorageSetItem('time-page-sort-ascending', true);
			setSortAscending(true);
		}
		Util.localStorageSetItem('time-page-sort', col);
		setSortValue(col);
	};

	const headerDayClick = day => {
		selectDayFromCalendar(day, false);
	};

	const getHeaderElemForCol = col => {
		if (!col.checked) return null;
		const sortClass = 'sortable ' + (sortValue === col.name ? (sortAscending ? 'ascending' : 'descending') : '');
		switch (col.name) {
			case COLUMN_NAME.PROJECT_INDICATOR:
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)}>
						<div title={formatMessage({id: 'common.project_id_short'})}>{formatMessage({id: 'common.project_id_short'})}</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case COLUMN_NAME.PROJECT_NAME:
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)} tabIndex="0">
						<div className={'header-text'} title={formatMessage({id: 'common.project'})}>
							{formatMessage({id: 'common.project'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'task-name':
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)} tabIndex="0">
						<div className={'header-text'} title={formatMessage({id: 'common.task'})}>
							{formatMessage({id: 'common.task'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'milestone':
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)}>
						<div className={'header-text'} title={formatMessage({id: 'common.milestone'})}>
							{formatMessage({id: 'common.milestone'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'client':
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)}>
						<div className={'header-text'} title={formatMessage({id: 'common.client'})}>
							{formatMessage({id: 'common.client'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'remaining':
				return (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name + ' ' + sortClass} onClick={changeSortValue.bind(this, col.name)}>
						<div className={'header-text'} title={formatMessage({id: 'common.remaining'})}>
							{formatMessage({id: 'common.remaining'})}
						</div>
						<span>&nbsp;&nbsp;</span>
					</div>
				);
			case 'hour-inputs':
				return isWeekView ? (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}>
						{weekDays.map((day, idx) => {
							const nonWorkingDay = holidays[idx].length > 0 || workingHourForTheDay(selectedPerson, day) === 0;

							return (
								<div
									key={'header-' + col.name + day.format('ll')}
									className={'week-day-header' + (Moment(day).isSame(Moment(), 'day') ? ' today' : '') + (nonWorkingDay ? ' non-working-day' : '')}
									onClick={headerDayClick.bind(this, day)}
								>
									<div className={'weekday'}>{day.format('dddd')}</div>
									<div className={'date-text'}>{day.format('DD MMM')}</div>
								</div>
							);
						})}
					</div>
				) : (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}>
						<div className={'time-header'}>{formatMessage({id: 'common.time_entry'})}</div>
					</div>
				);
			case 'notes':
				return isWeekView ? (
					<div key={'header-' + col.name} className={'header-elem-wrapper week-view-notes-wrapper'}>
						<div className={'header-text'} title={formatMessage({id: 'common.notes'})}>
							{formatMessage({id: 'common.notes'})}
						</div>
					</div>
				) : (
					<div key={'header-' + col.name} className={'header-elem-wrapper day-view-notes-wrapper'}>
						<div className={'header-text'} title={formatMessage({id: 'common.notes'})}>
							{formatMessage({id: 'common.notes'})}
						</div>
					</div>
				);
			case 'harvest':
				return viewer.company.harvestEnabled && viewer.harvestUser ? (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}>
						<div className={'header-text'} title={formatMessage({id: 'title.app_harvest'})}>
							{formatMessage({id: 'title.app_harvest'})}
						</div>
					</div>
				) : null;
			case 'unit4':
				return viewer.company.unit4Enabled && selectedPerson.unit4User ? (
					<div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}>
						<div className={'header-text'} title="Unit4">
							Unit4
						</div>
					</div>
				) : null;
			case 'star':
				return <div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}/>;
			case 'context-menu':
				return <div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}/>;
			case 'switch-task-indicator':
				return <div key={'header-' + col.name} className={'header-elem-wrapper ' + col.name}/>;
			default:
		}
	};

	const deleteTimeRegistration = lineItem => {
		Util.CommitMutation(
			DeleteTimeRegistration,
			{
				id: lineItem.id,
				taskId: lineItem.task ? lineItem.task.id : undefined,
				projectId: lineItem.project ? lineItem.project.id : undefined,
				idleTimeId: lineItem.idleTime ? lineItem.idleTime.id : undefined,
				companyId: viewer.company.id,
			},
			onTimeDeleteSuccess
		);
	};

	const deleteTimeRegistrationsForWeek = dayRegsForWeek => {
		const ids = [];
		dayRegsForWeek.map(day => day.map(reg => ids.push(reg.node.id)));
		Util.CommitMutation(DeleteAllTimeRegistrationsForWeek, {ids}, onTimeDeleteSuccess);
	};

	const editTimeRegistration = lineItem => {
		const lineItemProject = lineItem.task ? lineItem.task.project : lineItem.project;
		const passedDate = lineItem.year && lineItem.month && lineItem.day ? Util.CreateNonUtcMomentDate(lineItem.year, lineItem.month, lineItem.day) : null;
		showModal({
			type: MODAL_TYPE.TIMER_V3,
			updateTimeRegId: lineItem.id,
			isPreviousTimerReminder: false,
			timerProject: lineItemProject,
			timerTask: lineItem.task,
			timerIdleTime: lineItem.idleTime,
			passedNotes: lineItem.notes,
			passedTime: lineItem.minutesRegistered,
			passedDate: passedDate,
			useTimer: false,
			harvestTaskSelected: lineItem.harvestTaskIdInt ? btoa(`HarvestTaskType:${lineItem.harvestTaskIdInt}`) : null,
			selectedPerson: selectedPerson,
		});
	};

	const showDeleteConfirmationModal = (lineItem, isWeek) => {
		showModal({
			type: MODAL_TYPE.GENERIC,
			content: (
				<div className="default-warning-modal">
					<Warning messageId="common.delete-confirmation-title" />
					<div className="warning-part-2">{formatMessage({id: isWeek ? 'common.warning.week_delete_this_action_can_not_be_undone' : 'common.warning.this_action_can_not_be_undone'})}</div>
				</div>
			),
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.delete'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: !isWeek ? deleteTimeRegistration.bind(this, lineItem) : deleteTimeRegistrationsForWeek.bind(this, lineItem),
					cy: 'new-ui-time-custom-button-container',
				},
			],
		});
	};

	/**
	 * Returns whether context (action) menu should be visible or not
	 * @param {*} lineItem the item containing all time registrations
	 * @param {*} daysRegisteredForEntity
	 * @param {boolean} isWeekView
	 * @returns {boolean}
	 */
	const isContextVisible = (lineItem, daysRegisteredForEntity, isWeekView) => {
		if (lineItem.id === -1) return false;
		let isVisible = false;

		const isTimeReg = lineItem.type != null;
		if (isWeekView) {
			const dayRegList = daysRegisteredForEntity ? daysRegisteredForEntity : [[]];
			if (dayRegList.some(reg => reg.length > 0)) isVisible = true;
			const inLockedPeriodWeek = isTimeReg && dayRegList.some(dayRegs => timeEntryInLockedPeriod(lineItem, dayRegs, isWeekView));
			const invoicedWeek = isTimeReg && dayRegList.some(dayRegs => timeEntryInvoiced(lineItem, dayRegs, isWeekView));
			const hasAllocationWeek = lineItem.timeReg && lineItem.timeReg.node.allocationId && !(lineItem.timeReg.node.idleTime && lineItem.timeReg.node.idleTime.isInternalTime);
			const hasMultipleHarvestTasks = isTimeReg && dayRegList.some(dayRegs => timeEntryContainsHarvestTask(dayRegs));

			isVisible = isVisible && !hasAllocationWeek && !hasMultipleHarvestTasks && !inLockedPeriodWeek && !invoicedWeek;
		} else {
			if (lineItem.timeRegistrationsWithNotes && lineItem.timeRegistrationsWithNotes.length > 0) isVisible = true;
			if (lineItem.minutesRegistered !== undefined && lineItem.minutesRegistered !== null) isVisible = true;
			const inLockedPeriod = isTimeReg && timeEntryInLockedPeriod(lineItem, [], isWeekView);
			const invoiced = isTimeReg && timeEntryInvoiced(lineItem, [], isWeekView);
			const hasAllocation = lineItem.allocationId && !(lineItem.idleTime && lineItem.idleTime.isInternalTime);
			isVisible = isVisible && !inLockedPeriod && !invoiced && !hasAllocation;
		}

		// general visibility checks for both week and day rows
		const noAccess = isTimeReg && timeEntryNoAccess(lineItem);
		const projectDone = isTimeReg && timeEntryProjectDone(lineItem);
		const projectHalted = isTimeReg && timeEntryProjectHalted(lineItem);

		isVisible = isVisible && !noAccess && !projectDone && !projectHalted;

		return isVisible;
	};

	/**
	 * Returns an array of options for the Action Context Menu
	 * @param {object} lineItem
	 * @param {boolean} isWeekView
	 * @returns {Array}
	 */
	const getActionMenuOptions = (lineItem, isWeekView, dayRegsForEntitiy) => {
		let options;
		if (isWeekView) {
			options = [{text: formatMessage({id: isWeekView ? 'common.delete_row' : 'common.delete'}), onClick: showDeleteConfirmationModal.bind(this, dayRegsForEntitiy, isWeekView)}];
		} else {
			const lineItemProject = lineItem.task ? lineItem.task.project : lineItem.project;
			const lineItemProjectLocked = lineItemProject ? lineItemProject.status === PROJECT_STATUS.DONE || lineItemProject.status === PROJECT_STATUS.HALTED : false;
			const lineItemUnit4Locked = lineItem.unit4ActivityId || (lineItemProject && lineItemProject.unit4Project);
			const disabledTitle = lineItemProjectLocked ? 'You cannot edit a time registration on a done or halted project' : lineItemUnit4Locked ? 'You cannot edit a time registration with a Unit4 activity attached' : '';

			options = [
				{
					text: formatMessage({id: 'time_registration.edit.header'}),
					onClick: editTimeRegistration.bind(this, lineItem) || lineItemUnit4Locked,
					disabledTitle,
				},
				{text: formatMessage({id: 'common.delete'}), onClick: showDeleteConfirmationModal.bind(this, lineItem)},
			];
		}
		return options;
	};

	const isDoneOrHalted = project => {
		return project ? project.status === 'DONE' || project.status === 'HALTED' : false;
	};

	const shouldDisableHarvestPopup = (lineItem, isWeekView) => {
		const isHarvestReg = isWeekView ? lineItem.harvestProject || (lineItem.project && lineItem.project.harvestProject) : lineItem.harvestTaskIdInt;
		return !isHarvestReg || (lineItem.type === 'task' && !lineItem.task.fullAccessToProject) || isDoneOrHalted(lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : null);
	};

	const shouldDisableUnit4Popup = (lineItem, isWeekView) => {
		const isUnit4Reg = isWeekView
			? lineItem.unit4Project || (lineItem.project && lineItem.project.unit4Project)
			: lineItem.unit4ActivityId || (lineItem.task && lineItem.task.project && lineItem.task.project.unit4Project && lineItem.task.project.unit4Project.activitiesEnabled);

		return !isUnit4Reg || (lineItem.type === 'task' && !lineItem.task.fullAccessToProject) || isDoneOrHalted(lineItem.task ? lineItem.task.project : lineItem.project ? lineItem.project : null);
	};

	const getRowElemForCol = (col, lineItem, dayRegsForEntity, rowNr, groupedRegsMap, indexIndicator, switchTaskType) => {
		if (!col.checked) return null;
		// The note fiesta is simply too much so checking here and only passing down lineItem without nodes.
		const lineItemNode = lineItem.node ? lineItem.node : lineItem;
		const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;
		const isTimeReg = lineItemNode.type != null;
		let task;
		let project;
		let idleTime;
		let nothing;

		if (isTimeReg) {
			if (isWeekView && dayRegsForEntity != null) {
				// We aren't given time regs, we are given entities under time regs
				if (lineItemNode.type === TIME_REG_TYPES.NO_ACCESS) {
					nothing = true;
				}
				if (lineItemNode.type === TIME_REG_TYPES.TASK_TIME) {
					task = lineItemNode;
					project = task.project;
				} else if (lineItemNode.type === TIME_REG_TYPES.PROJECT_TIME) {
					project = lineItemNode;
					task = null;
				} else if (lineItemNode.type === TIME_REG_TYPES.IDLE_TIME) {
					idleTime = lineItemNode;
				}
			} else {
				// We aren given time regs (day view)
				nothing = lineItemNode.type === TIME_REG_TYPES.NO_ACCESS;
				task = lineItemNode.type === TIME_REG_TYPES.TASK_TIME ? lineItemNode.task : null;
				project = task ? task.project : lineItemNode.type === TIME_REG_TYPES.PROJECT_TIME ? lineItemNode.project : null;
				idleTime = lineItemNode.type === TIME_REG_TYPES.IDLE_TIME ? lineItemNode.idleTime : null;
			}
		} else {
			// For non-timeregs, our entity is either an internal time or a task
			if (lineItemNode.switchIdleTime) {
				idleTime = lineItemNode;
			} else {
				task = lineItemNode.task ? lineItemNode.task : lineItemNode;
				project = task.project;
			}
		}

		const firstElem = enabledColumns.find(c => c.checked);
		const lastElem = enabledColumns[enabledColumns.length - 1];
		const isFavorited = task ? task.favoured : idleTime ? idleTime.favoured : false;
		const shouldFocus = task && timeRegToFocus === task.id;

		// Checks for disabled rows
		const projectDone = isTimeReg && timeEntryProjectDone(lineItemNode);
		const projectHalted = isTimeReg && timeEntryProjectHalted(lineItemNode);
		const noAccess = isTimeReg && timeEntryNoAccess(lineItemNode);
		const inLockedPeriod = isTimeReg && timeEntryInLockedPeriod(lineItemNode, [], isWeekView);
		const invoiced = isTimeReg && timeEntryInvoiced(lineItemNode, [], isWeekView);
		const isInternalTimeDisabled = isTimeReg && lineItemNode.idleTime && lineItemNode.idleTime.disabled;
		const hasAllocation = lineItemNode.allocationId && !(lineItemNode.idleTime && lineItemNode.idleTime.isInternalTime);

		const fullAccessToProject = project && project.fullAccessToProject;

		let additionalClasses = (isFavorited ? ' favorite' : '') + (shouldFocus ? ' focused' : '');
		if (col === firstElem) {
			additionalClasses += ' first';
		} else if (col === lastElem) {
			// must be the star column or the context menu
			additionalClasses += ' last';
		}
		let dayLockedInTime = !isWeekView && lockedDate && lockedDate.isSameOrAfter(currentViewingDate, 'day');
		let elem;
		switch (col.name) {
			case COLUMN_NAME.PROJECT_INDICATOR:
				elem = (
					<div id={`project-indicator-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper project-indicator' + additionalClasses}>
						{project && <ProjectIndicator project={project} clearSubPath={true} disableLink={!fullAccessToProject} noTitle={!fullAccessToProject} />}
					</div>
				);
				break;
			case COLUMN_NAME.PROJECT_NAME:
				const projectName = project ? (fullAccessToProject ? project.name : intl.formatMessage({id: 'timesheets.no_access'})) : lineItemNode.type === TIME_REG_TYPES.NO_ACCESS ? intl.formatMessage({id: 'timesheets.no_access'}) : '';
				elem = (
					<a
						id={`project-name-${indexIndicator}`}
						href={fullAccessToProject ? getNavigationString(project, true) : null}
						title={fullAccessToProject ? projectName : null}
						key={lineItemNode.id + col.name}
						className={'elem-wrapper ' + col.name + ' ' + additionalClasses + (fullAccessToProject ? ' clickable' : '') + (nothing || !fullAccessToProject ? ' no-access' : '')}
						tabIndex="0"
					>
						{projectName}
					</a>
				);
				break;
			case 'task-name':
				const taskName = task ? task.name : lineItemNode.type === TIME_REG_TYPES.IDLE_TIME ? (isWeekView ? idleTime.name : lineItemNode.idleTime.name) : lineItemNode.switchIdleTime ? lineItemNode.name : '';
				elem = (
					<div
						data-cy={'time-reg-table-task-name-cell'}
						id={`task-name-${indexIndicator}`}
						title={task ? 'T' + task.companyTaskId + ' - ' + task.name : ''}
						key={lineItemNode.id + col.name}
						className={'elem-wrapper task-name ' + additionalClasses + (task && task.fullAccessToProject ? ' clickable' : '')}
						tabIndex="0"
						onClick={task && task.fullAccessToProject ? () => Util.showTaskModal(task.companyTaskId, history) : null}
					>
						{taskName}
					</div>
				);
				break;
			case 'milestone':
				const milestoneLocation =
					task && task.phase && project ? '/project/P-' + project.companyProjectId + '/scoping#' + task.phase.id : task && task.phase && task.project ? '/project/P-' + task.project.companyProjectId + '/scoping#' + task.phase.id : null;
				elem = (
					<a
						id={`milestone-${indexIndicator}`}
						title={task && task.phase && task.phase.name}
						key={lineItemNode.id + col.name}
						className={'elem-wrapper task-milestone ' + additionalClasses + (task && task.phase ? ' clickable' : '')}
						href={milestoneLocation}
						disabled={!milestoneLocation}
					>
						{task && task.phase && task.phase.name}
					</a>
				);
				break;
			case 'client':
				elem = (
					<div id={`client-${indexIndicator}`} title={project && project.client && project.client.name} key={lineItemNode.id + col.name} className={'elem-wrapper project-client-tile ' + additionalClasses}>
						{project && project.client && project.client.name}
					</div>
				);
				break;
			case 'remaining':
				elem = (
					<div id={`remaining-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper task-remaining-tile ' + additionalClasses}>
						{task && project && project.fullAccessToProject
							? task.timeLeft != null
								? project.estimationUnit === 'POINTS'
									? formatNumber((task.timeLeft * 10) / 10, {format: 'rounded_two_decimal'}) + ' ' + formatMessage({id: 'common.points.short'})
									: Util.convertMinutesToFullHour(task.timeLeft, intl, true)
								: null
							: null}
					</div>
				);
				break;
			case 'hour-inputs':
				const dayRegList = dayRegsForEntity ? dayRegsForEntity : [[], [], [], [], [], [], []];
				// Only show value for timeRegs or non-empty moved tasks

				if (isWeekView) {
					const showValueForWeekView = dayRegs => isTimeReg && dayRegList.some(el => el.length > 0) && dayRegs.length > 0;
					elem = (
						<div id={`hour-inputs-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper week-view hours-input-wrapper' + additionalClasses}>
							{dayRegList.map((dayRegs, idx) => {
								const dayOfInput = weekStart.clone().add(idx, 'd');
								const inLockedPeriodWeek = isTimeReg && timeEntryInLockedPeriod(lineItemNode, dayRegs, isWeekView);
								const approvalStatus = usingTimeApproval && getTimeEntryApprovalStatus(lineItemNode, dayRegs, isWeekView);
								const invoicedWeek = isTimeReg && timeEntryInvoiced(lineItemNode, dayRegs, isWeekView);
								const hasAllocationWeek = lineItemNode.timeReg && lineItemNode.timeReg.node.allocationId && !(lineItemNode.timeReg.node.idleTime && lineItemNode.timeReg.node.idleTime.isInternalTime);
								const containsNotes = isTimeReg && timeEntryContainsNotes(lineItemNode, dayRegs);
								const hasMultipleHarvestTasks = isTimeReg && timeEntryContainsHarvestTask(dayRegs);
								const nonWorking = holidays[idx].length > 0 || workingHourForTheDay(selectedPerson, dayOfInput) === 0;
								const isInternalTimeDisabled = isTimeReg && lineItemNode.timeReg.node.idleTime && lineItemNode.timeReg.node.idleTime.disabled;
								const lockedInTime = lockedDate && lockedDate.isSameOrAfter(dayOfInput, 'day');
								const infoText = getInfoTextForHoursInput(
									noAccess,
									inLockedPeriodWeek,
									approvalStatus,
									invoicedWeek,
									projectDone,
									projectHalted,
									containsNotes,
									hasMultipleHarvestTasks,
									hasAllocationWeek,
									isWeekView,
									isInternalTimeDisabled,
									lockedInTime,
									periodTimeApprovalStatus,
									intl
								);
								return (
									<HoursInputView
										infoText={infoText}
										cy={'time-entry-hours-input'}
										index={idx}
										disabled={inLockedPeriodWeek || invoicedWeek || lockedInTime || nothing || noAccess || hasAllocationWeek || projectHalted || projectDone || containsNotes || hasMultipleHarvestTasks || isInternalTimeDisabled}
										nonWorkingDay={nonWorking}
										forceUpdate={forceUpdate}
										parentKeyDown={e => onInputKeyDown(e, rowNr * 7 + idx)}
										innerRef={div => (hourInputs[rowNr * 7 + idx] = div)}
										key={currentViewingDate.format('x') + lineItemNode.id + col.name + '-day-' + idx}
										value={showValueForWeekView(dayRegs) ? dayRegs.reduce((acc, day) => day.node.minutesRegistered + acc, 0) / 60 : null}
										minuteLimit={1440}
										mutationValidChange={true}
										mutation={minutes => handleHoursInputChange(minutes, lineItemNode, idx, isWeekView, groupedRegsMap, selectedPerson)}
										userpilot={'task-row-hours-input'}
									/>
								);
							})}
						</div>
					);
				} else {
					const approvalStatus = usingTimeApproval && getTimeEntryApprovalStatus(lineItemNode, [], isWeekView);
					elem = (
						<div id={`hour-inputs-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper day-view hours-input-wrapper' + additionalClasses}>
							<HoursInputView
								key={currentViewingDate.format('x') + indexIndicator}
								infoText={getInfoTextForHoursInput(
									noAccess,
									inLockedPeriod,
									approvalStatus,
									invoiced,
									projectDone,
									projectHalted,
									false,
									false,
									hasAllocation,
									isWeekView,
									isInternalTimeDisabled,
									dayLockedInTime,
									periodTimeApprovalStatus,
									intl
								)}
								disabled={inLockedPeriod || invoiced || dayLockedInTime || nothing || noAccess || projectHalted || projectDone || hasAllocation || isInternalTimeDisabled}
								nonWorkingDay={holidays.length > 0 || workingHourForTheDay(selectedPerson, currentViewingDate) === 0}
								forceUpdate={forceUpdate}
								parentKeyDown={e => onInputKeyDown(e, rowNr * 7)}
								innerRef={div => (hourInputs[rowNr * 7] = div)}
								value={lineItemNode.minutesRegistered || isTimeReg ? lineItemNode.minutesRegistered / 60 : undefined}
								minuteLimit={1440}
								mutationValidChange={true}
								mutation={minutes => handleHoursInputChange(minutes, lineItemNode, null, isWeekView, null, selectedPerson)}
								cy={'time-entry-hours-input'}
								userpilot={'task-row-hours-input'}
							/>
						</div>
					);
				}
				break;
			case 'notes':
				elem = isWeekView ? (
					<div id={`notes-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper week-view-notes-wrapper' + additionalClasses}>
						<NotesPopOut
							locked={nothing}
							disabled={task && !task.fullAccessToProject}
							characterLimit={viewer.company.characterLimit}
							timeRegItem={lineItemWithNotes(lineItemNode, dayRegsForEntity)}
							taskName={task ? task.name : project ? project.name : idleTime ? idleTime.name : ''}
							lockedDate={lockedDate}
						/>
					</div>
				) : (
					<div id={`notes-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper day-view-notes-wrapper' + additionalClasses + (invoiced || inLockedPeriod ? ' disabled' : '')} data-cy={'notes-switch-task'}>
						<TextAreaFoldout
							value={isTimeReg ? lineItemNode.notes : lineItemNode.notes ? lineItemNode.notes : ''}
							onBlur={val => handleNotesFieldBlur(lineItemNode, val, currentViewingDate.clone(), isTimeReg)}
							disabled={dayLockedInTime || nothing || noAccess || invoiced || inLockedPeriod}
							maxChars={viewer.company.characterLimit}
						/>
					</div>
				);
				break;
			case 'harvest':
				elem =
					viewer.company.harvestEnabled && viewer.harvestUser ? (
						getIsHarvestLineItem(lineItemNode) ? (
							<div key={lineItemNode.id + col.name} className={'elem-wrapper harvest' + additionalClasses}>
								<HarvestPopOut
									disabled={!viewer.harvestUser || shouldDisableHarvestPopup(lineItemNode, isWeekView)}
									timeRegistrationsWithNotes={isWeekView ? lineItemWithNotes(lineItemNode, dayRegsForEntity).timeRegistrationsWithNotes : lineItemNode.minutesRegistered ? [lineItemNode] : null}
									loadOnExpand={true}
									timeRegItem={lineItemNode}
									viewDate={currentViewingDate.clone()}
									selectedPerson={selectedPerson}
									taskName={lineItemNode.task ? lineItemNode.task.name : lineItemNode.project ? lineItemNode.project.name : lineItemNode.idleTime ? lineItemNode.idleTime.name : lineItemNode.name}
									harvestTasks={harvestTasks}
									updateHarvestTasks={fetchHarvestTasks.bind(this, lineItemNode.task ? lineItemNode.task.project : lineItemNode.project ? lineItemNode.project : lineItemNode)}
									integrationLoading={harvestLoading}
									lockedDate={lockedDate}
								/>
							</div>
						) : (
							<div key={lineItemNode.id + col.name} className={'elem-wrapper' + additionalClasses}/>
						)
					) : null;
				break;
			case 'unit4':
				elem =
					viewer.company.unit4Enabled && selectedPerson.unit4User ? (
						getIsUnit4LineItem(lineItemNode) ? (
							<div key={lineItemNode.id + col.name} className={'elem-wrapper unit4' + additionalClasses}>
								<Unit4PopOut
									disabled={!selectedPerson.unit4User || shouldDisableUnit4Popup(lineItemNode, isWeekView)}
									timeRegistrations={isWeekView ? lineItemWithNotes(lineItemNode, dayRegsForEntity).timeRegistrationsWithNotes : lineItemNode.minutesRegistered ? [lineItemNode] : null}
									loadOnExpand={true}
									timeRegItem={lineItemNode}
									viewDate={currentViewingDate.clone()}
									selectedPerson={selectedPerson}
									taskName={lineItemNode.task ? lineItemNode.task.name : lineItemNode.project ? lineItemNode.project.name : lineItemNode.idleTime ? lineItemNode.idleTime.name : lineItemNode.name}
									unit4Activities={unit4Activities}
									updateUnit4Activities={fetchUnit4Activities.bind(this, lineItemNode.task ? lineItemNode.task.project : lineItemNode.project ? lineItemNode.project : lineItemNode)}
									integrationLoading={unit4Loading}
									lockedDate={lockedDate}
								/>
							</div>
						) : (
							<div key={lineItemNode.id + col.name} className={'elem-wrapper' + additionalClasses}/>
						)
					) : null;
				break;
			case 'star':
				elem = (
					<div id={`star-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper star' + additionalClasses}>
						{((task && task.fullAccessToProject) || idleTime) && <StarTask parentId={viewer.id} task={task || idleTime} isIdleTime={!!idleTime}/>}
					</div>
				);
				break;
			case 'context-menu':
				const locked = isWeekView ? lockedDate && lockedDate.isSameOrAfter(currentViewingDate.clone().startOf('week'), 'day') : dayLockedInTime;
				elem = (
					<div id={`context-menu-${indexIndicator}`} key={lineItemNode.id + col.name} className={'elem-wrapper context-menu' + additionalClasses}>
						{isContextVisible(lineItemNode, dayRegsForEntity, isWeekView) && !locked && <ActionsMenu whiteInner={true} options={getActionMenuOptions(lineItemNode, isWeekView, dayRegsForEntity)} />}
					</div>
				);
				break;
			case 'switch-task-indicator':
				elem = (
					<div key={lineItemNode.id + col.name} className={'elem-wrapper switch-task-indicator' + additionalClasses}>
						{switchTaskType === 'suggested' && <AiIcon opacity={0.39} useNewIcon={true} />}
					</div>
				);
				break;
			default:
		}
		return elem;
	};

	const getFooterColumn = (enabledColumns, timeAndWorkingHoursPerDay, isWeekView) => {
		const firstElem = enabledColumns.find(c => c.checked);
		const lastElem = enabledColumns[enabledColumns.length - 1];

		return enabledColumns.map(col => {
			// return if column is not shown
			if (!col.checked) {
				return null;
			}

			// for adding borders and padding to first and last element
			let additionalClasses = '';
			if (col === firstElem) {
				additionalClasses += ' first';
			}
			if (col === lastElem) {
				additionalClasses += ' last';
			}

			switch (col.name) {
				case 'task-name': {
					const totals = timeAndWorkingHoursPerDay.reduce(
						(totals, entry) => {
							totals.time += entry.time;
							totals.hoursToWork += entry.hoursToWork;
							return totals;
						},
						{time: 0, hoursToWork: 0}
					);

					if (isWeekView) {
						return (
							<div key={'totals-' + col.name}
								 className={'totals-elem-wrapper total-view hours-total-wrapper overflow-visible' + additionalClasses}>
								<p className="hours-total-week">Total:</p>
								<CheckMarkAndTime dataCy="timesheets-total-for-view" time={totals.time}
												  hoursToWork={totals.hoursToWork} intl={intl} overflow={true}
												  locked={isFullyLocked}/>
							</div>
						);
					} else {
						return (
							<div key={'totals-' + col.name}
								 className={'totals-elem-wrapper total-view hours-total-wrapper overflow-visible' + additionalClasses}>
								<p>Total:</p>
							</div>
						);
					}
				}
				case 'hour-inputs': {
					if (isWeekView) {
						return (
							<div key={'totals-' + col.name}
								 className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}>
								{timeAndWorkingHoursPerDay.map((item, i) => {
									return item.hoursToWork > 0 || item.noOfTimeRegistrations > 0 ? (
										<CheckMarkAndTime key={i} time={item.time} hoursToWork={item.hoursToWork}
														  showTime={!item.isHoliday}
														  locked={lockedDate && lockedDate.isSameOrAfter(item.day, 'day')}/>
									) : (
										<div key={i}/>
									);
								})}
							</div>
						);
					} else {
						return (
							<div key={'totals-' + col.name}
								 className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}>
								{timeAndWorkingHoursPerDay.map((item, i) => (
									<CheckMarkAndTime dataCy="timesheets-total-for-view" key={i} time={item.time}
													  hoursToWork={item.hoursToWork} showTime={!item.isHoliday}
													  locked={isFullyLocked}/>
								))}
							</div>
						);
					}
				}
				case 'harvest': {
					return viewer.company.harvestEnabled && viewer.harvestUser ? <div key={'totals-' + col.name}
																					  className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}/> : null;
				}
				case 'unit4': {
					return viewer.company.unit4Enabled && selectedPerson.unit4User ? <div key={'totals-' + col.name}
																						  className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}/> : null;
				}

				// Not using these columns, so just throw a standard div
				case COLUMN_NAME.PROJECT_NAME:
				case COLUMN_NAME.PROJECT_INDICATOR:
				case 'milestone':
				case 'client':
				case 'remaining':
				case 'notes':
				case 'star':
				case 'context-menu': {
					return <div key={'totals-' + col.name}
								className={'elem-wrapper total-view hours-total-wrapper' + additionalClasses}/>;
				}
				case 'switch-task-indicator': {
					return <div key={'totals-' + col.name}
								className={'elem-wrapper total-view hours-total-wrapper switch-task-indicator' + additionalClasses}/>;
				}
				default:
					return null;
			}
		});
	};

	let rowNr = 0;
	const getDayRows = (sortedTimeRegsForPeriod, enabledColumns, day, selectedPerson, topSectionLength) => {
		const timeRows = [];
		addTimeRegTypes(sortedTimeRegsForPeriod).forEach((tReg, tRegIndex) => {
			if (tReg.node && tReg.node.task && tReg.node.task.project === null) return;
			timeRows.push(enabledColumns.map(col => getRowElemForCol(col, tReg, null, null, null, `time-reg-${tRegIndex}`)));
		});
		if (topSectionLength > 0 || timeRows.length > 0) {
			const totals = getTimeAndWorkingHoursForPeriod(day, day, timeRegistrations, selectedPerson);
			timeRows.push(getFooterColumn(enabledColumns, totals));
		}

		return timeRows;
	};

	const getWeekViewRows = topSectionLength => {
		let weekViewRows = [];
		if (isWeekView) {
			groupedRegs.forEach((dayRegsForEntitiy, entity) => {
				weekViewRows.push(enabledColumns.map(col => getRowElemForCol(col, entity, dayRegsForEntitiy, rowNr, groupedRegs, `time-reg-${rowNr}`)));
				rowNr++;
			});
			if (topSectionLength > 0 || weekViewRows.length > 0) {
				weekViewRows.push(getFooterColumn(enabledColumns, getTimeAndWorkingHoursForPeriod(weekStart, weekEnd, timeRegistrations, selectedPerson), isWeekView));
			}
		}
		return weekViewRows;
	};

	// These tasks are tasks that have been moved from tasks to time regs while the page was open
	const timeRegsMovedFromTasks = timeRegsForPeriod.filter(tReg => tReg.node.task && movedTasks.some(taskId => taskId === tReg.node.task.id));
	const timeRegsMovedFromIdleTimes = timeRegsForPeriod.filter(tReg => tReg.node.idleTime && movedIdleTimes.some(idleTimeId => idleTimeId === tReg.node.idleTime.id));
	const timeRegsMoved = timeRegsMovedFromTasks.concat(timeRegsMovedFromIdleTimes);
	const groupedMovedTasks = isWeekView ? groupRegs(addTimeRegTypes(timeRegsMoved)) : [];

	// Filter out switch tasks that are already in time regs
	const filteredSwitchTasks =
		selectedSwitch === 'suggested'
			? suggestedSwitchTasks
					.map(suggestion => suggestion.task)
					.filter(task => task && !timeRegsForPeriod.some(tReg => tReg.node.task && tReg.node.task.id === task.id))
					.filter(filterTimeregistrations.bind(this, searchValue))
			: selectedSwitch != null
			? switchTasks
					.filter(task => !timeRegsForPeriod.some(tReg => tReg.node.task && tReg.node.task.id === task.node.id))
					.filter(filterTimeregistrations.bind(this, searchValue))
					.map(task => task.node)
			: [];

	const filteredSwitchIdleTimes =
		selectedSwitch === 'suggested'
			? suggestedSwitchTasks
					.map(suggestion => suggestion.idleTime)
					.filter(idleTime => idleTime && !timeRegsForPeriod.some(tReg => tReg.node.idleTime && tReg.node.idleTime.id === idleTime.id))
					.filter(filterTimeregistrations.bind(this, searchValue))
					.map(idleTime => ({...idleTime, switchIdleTime: true}))
			: selectedSwitch === 'starred'
			? switchIdleTimes
					.filter(idleTime => idleTime && !timeRegsForPeriod.some(tReg => tReg.node.idleTime && tReg.node.idleTime.id === idleTime.node.id))
					.filter(filterTimeregistrations.bind(this, searchValue))
					.map(idleTime => ({...idleTime.node, switchIdleTime: true}))
			: [];

	// Switch tasks and moved tasks together
	const combinedSwitchList = isWeekView ? Array.from(groupedMovedTasks.keys()).concat(filteredSwitchTasks).concat(filteredSwitchIdleTimes) : addTimeRegTypes(timeRegsMoved).concat(filteredSwitchTasks).concat(filteredSwitchIdleTimes);

	const topSection = !isFullyLocked ? (isWeekView ? sortList(combinedSwitchList, sortValue, sortAscending) : sortList(combinedSwitchList, sortValue, sortAscending)) : [];
	const topSectionRows = [];
	topSection.forEach(entity => {
		if (entity) {
			let dayRegsForEntity = isWeekView && groupedMovedTasks.get(entity) ? groupedMovedTasks.get(entity) : [[], [], [], [], [], [], []];
			if (entity.minutesRegistered) {
				if (isWeekView) {
					// Important to use weekday instead of day, since weekday returns day index relative to locale
					const dayOfWeek = Util.CreateNonUtcMomentDate(entity.year, entity.month, entity.day).weekday();

					dayRegsForEntity[dayOfWeek] = [entity];
				} else {
					dayRegsForEntity = null;
				}
			}
			topSectionRows.push(enabledColumns.map(col => getRowElemForCol(col, entity, dayRegsForEntity, rowNr, groupedMovedTasks, `switch-task-${rowNr}`, selectedSwitch)));
			rowNr++;
		}
	});

	const gridStyle = {
		// Build grid depending on enabled columns
		'--gridCols': enabledColumns.reduce((acc, col) => {
			if (!col.checked) return acc;
			if (col.name === 'harvest' && (!viewer.company.harvestEnabled || !viewer.harvestUser)) return acc;
			if (col.name === 'unit4' && (!viewer.company.unit4Enabled || !selectedPerson.unit4User)) return acc;
			switch (col.name) {
				case 'switch-task-indicator':
					acc += '44px';
					break;
				case COLUMN_NAME.PROJECT_INDICATOR:
					acc += ' 55px';
					break;
				case 'task-name':
					acc += ' 1fr';
					break;
				case COLUMN_NAME.PROJECT_NAME:
				case 'milestone':
				case 'client':
				case 'remaining':
				case 'harvest':
				case 'unit4':
				case 'notes':
					acc += ' auto';
					break;
				case 'hour-inputs':
					acc += ' min-content';
					break;
				case 'star':
				case 'context-menu':
					acc += ' 30px';
					break;
				default:
					break;
			}
			return acc;
		}, ''),
	};

	const rows = isWeekView ? getWeekViewRows(topSection.length) : getDayRows(sortedTimeRegsForPeriod, enabledColumns, currentViewingDate, selectedPerson, topSection.length);

	return (
		<TimeRowTableWrapper>
			{switchTasksLoading ? (
				<InlineLoader />
			) : rows.length === 0 && topSection.length === 0 ? (
				<EmptyState pageName={EMPTY_STATE.TIMESHEETS} filterOrSearchActive={searchValue}/>
			) : (
				<div key={'grid-wrapper'} className={'grid-wrapper'} style={gridStyle}>
					{enabledColumns.map(col => getHeaderElemForCol(col))}
					{selectedSwitch && topSectionRows}
					{rows}
				</div>
			)}
		</TimeRowTableWrapper>
	);
};

const timesheetsTimeRowTableQuery = graphql`
	query timesheetsTimeRowTable_Query($taskTypeSearchQuery: TaskSearchQueryType, $fetchSwitchTasks: Boolean!, $sortValue: String, $fetchSuggestedTasks: Boolean!) {
		viewer {
			actualPersonId
			component(name: "timesheet_time_row_table_section")
			...timesheetsTimeRowTable_viewer @arguments(taskTypeSearchQuery: $taskTypeSearchQuery, fetchSwitchTasks: $fetchSwitchTasks, sortValue: $sortValue, fetchSuggestedTasks: $fetchSuggestedTasks)
		}
	}
`;

export {timesheetsTimeRowTableQuery};

export default withRouter(
	withSocketHandling(
		createRefetchContainer(
			timesheetsTimeRowTable,
			{
				viewer: graphql`
					fragment timesheetsTimeRowTable_viewer on Viewer
					@argumentDefinitions(taskTypeSearchQuery: {type: "TaskSearchQueryType"}, fetchSwitchTasks: {type: "Boolean!"}, sortValue: {type: "String"}, fetchSuggestedTasks: {type: "Boolean!"}) {
						id
						actualPersonId
						harvestUser
						unit4User
						company {
							id
							harvestEnabled
							unit4Enabled
							taskTypeQueryTasks: allTasks(first: 1000, searchQuery: $taskTypeSearchQuery, sortValue: $sortValue, approvedOnly: true, runningProjectOnly: true)
								@connection(key: "Company_taskTypeQueryTasks", filters: [])
								@include(if: $fetchSwitchTasks) {
								pageInfo {
									endCursor
									hasNextPage
								}
								edges {
									node {
										id
										userCanDeleteTask
										userCantDeleteTaskReason
										name
										latestUiUpdateAt
										startDay
										startMonth
										startYear
										deadlineYear
										deadlineMonth
										deadlineDay
										timeLeft
										companyTaskId
										favoured
										highPriority
										bug
										blocked
										approved
										canStart
										canBeSetToDone
										fullAccessToProject
										parentTaskId
										statusColumnV2 {
											id
											name
											category
										}
										assignedPersons {
											id
											firstName
											lastName
											email
											profilePictureId
											profilePictureDefaultId
											permissions
										}
										project {
											id
											name
											companyProjectId
											minutesPerEstimationPoint
											estimationUnit
											projectColor
											projectEndYear
											projectEndMonth
											projectEndDay
											taskLevels
											status
											fullAccessToProject
											harvestProject {
												id
											}
											unit4Project {
												id
												activitiesEnabled
											}
											client {
												id
												name
											}
										}
										phase {
											id
											name
										}
									}
									cursor
								}
							}
							taskTypeQueryIdleTimes: idleTimes(first: 100000, favouredOnly: true) @include(if: $fetchSwitchTasks) @connection(key: "Company_taskTypeQueryIdleTimes", filters: []) {
								edges {
									node {
										id
										name
										isInternalTime
										favoured
										disabled
									}
								}
							}
						}
						timeRegistrationSuggestedTasks @include(if: $fetchSuggestedTasks) {
							id
							name
							task {
								id
								name
								progress
								companyTaskId
								highPriority
								approved
								timeLeft
								favoured
								fullAccessToProject
								latestUiUpdateAt
								timeLeft
								parentTaskId
								project {
									id
									name
									status
									companyProjectId
									projectColor
									estimationUnit
									fullAccessToProject
									client {
										id
										name
									}
									harvestProject {
										id
										name
									}
									unit4Project {
										id
										name
									}
								}
								phase {
									id
									name
								}
							}
							idleTime {
								id
								name
								isInternalTime
								favoured
							}
							total
							reasons {
								id
								label
								value
								sentenceId
							}
						}
						taskSuggestionsRecommender @include(if: $fetchSuggestedTasks) {
							recommendationInstanceId
							recommendationId
							generation
							timeStep
						}
					}
				`,
			},
			graphql`
				query timesheetsTimeRowTableRefetchQuery($taskTypeSearchQuery: TaskSearchQueryType, $fetchSwitchTasks: Boolean!, $sortValue: String, $fetchSuggestedTasks: Boolean!) {
					viewer {
						component(name: "timesheet_time_row_table_section")
						...timesheetsTimeRowTable_viewer @arguments(taskTypeSearchQuery: $taskTypeSearchQuery, fetchSwitchTasks: $fetchSwitchTasks, sortValue: $sortValue, fetchSuggestedTasks: $fetchSuggestedTasks)
					}
				}
			`
		)
	)
);
